import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, PackageCheck } from "lucide-react";

export default function CompletePackageForm({ qr, pkg, onCancel, onSaved }) {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(qr.assigned_product_id || "");
  const [form, setForm] = useState({
    package_name: pkg?.package_name || (qr.assigned_product_name ? `${qr.assigned_product_name} package` : ""),
    description: pkg?.description || "",
    receiver_name: pkg?.receiver_name || "",
    receiver_phone: pkg?.receiver_phone || "",
    receiver_address: pkg?.receiver_address || "",
    special_instructions: pkg?.special_instructions || "",
  });
  const [items, setItems] = useState(() => {
    if (pkg?.items?.length) {
      return pkg.items.map(i => ({
        _uid: crypto.randomUUID(),
        item_name: i.item_name, quantity: i.quantity,
        description: i.description || "", product_id: i.product_id || "",
      }));
    }
    if (qr.assigned_product_id) {
      return [{
        _uid: crypto.randomUUID(),
        item_name: qr.assigned_product_name, quantity: 1,
        description: "", product_id: qr.assigned_product_id,
      }];
    }
    return [{ _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }];
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dispatcher/products").then(r => setProducts(r.data)).catch((e) => { console.debug("products fetch failed:", e); });
  }, []);

  const updateItem = (idx, patch) =>
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const onProductPick = (idx, pid) => {
    const p = products.find(x => x.id === pid);
    updateItem(idx, { product_id: pid, item_name: p ? p.name : "" });
  };

  const save = async (markPickup) => {
    if (!form.package_name || !form.receiver_name || !form.receiver_phone || !form.receiver_address) {
      toast.error("Fill in package name, receiver name, phone, and address.");
      return;
    }
    const cleanItems = items
      .filter(it => it.item_name?.trim())
      .map(it => ({ ...it, product_id: it.product_id || null, quantity: Number(it.quantity) || 1 }));
    if (cleanItems.length === 0) {
      toast.error("Add at least one item.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/dispatcher/qr/${qr.qr_code_id}/complete-package`, {
        product_id: productId || null,
        ...form,
        items: cleanItems,
      });
      toast.success("Package information saved.");
      onSaved(markPickup);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="complete-pkg-form">
      <DialogHeader>
        <DialogTitle className="font-display">Complete Package Information</DialogTitle>
        <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
          {qr.qr_code_id} · {qr.store_name}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 mt-3">
        <div className="grid grid-cols-2 gap-2 text-xs border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-100/40 dark:bg-slate-900/40">
          <div className="text-slate-500">QR Code ID</div>
          <div className="font-mono-tech text-cyan-300">{qr.qr_code_id}</div>
          <div className="text-slate-500">Store</div>
          <div className="text-slate-900 dark:text-white">{qr.store_name}</div>
        </div>

        <Field label="Assign Product (optional)">
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="cpf-product-select" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Package Name">
          <Input data-testid="cpf-name" value={form.package_name} onChange={e => setForm({...form, package_name: e.target.value})} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
        </Field>
        <Field label="Description (optional)">
          <Input data-testid="cpf-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
        </Field>
        <Field label="Receiver Name">
          <Input data-testid="cpf-receiver-name" value={form.receiver_name} onChange={e => setForm({...form, receiver_name: e.target.value})} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
        </Field>
        <Field label="Receiver Phone">
          <Input data-testid="cpf-receiver-phone" value={form.receiver_phone} onChange={e => setForm({...form, receiver_phone: e.target.value})} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
        </Field>
        <Field label="Receiver Address">
          <Textarea data-testid="cpf-receiver-address" value={form.receiver_address} onChange={e => setForm({...form, receiver_address: e.target.value})} rows={2} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>
        <Field label="Special Instructions (optional)">
          <Textarea data-testid="cpf-instructions" value={form.special_instructions} onChange={e => setForm({...form, special_instructions: e.target.value})} rows={2} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>

        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Items</Label>
            <Button size="sm" variant="outline" type="button" onClick={addItem} className="h-7 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it._uid || idx} className="border border-slate-200 dark:border-slate-800 rounded-md p-2 bg-slate-100/40 dark:bg-slate-900/40 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={it.product_id || ""} onValueChange={(v) => onProductPick(idx, v)}>
                    <SelectTrigger data-testid={`cpf-item-product-${idx}`} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                      <SelectValue placeholder="Pick product…" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                      {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`cpf-item-qty-${idx}`} type="number" min={1} value={it.quantity} onChange={e => updateItem(idx, { quantity: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" placeholder="Qty" />
                </div>
                <div className="flex gap-2">
                  <Input data-testid={`cpf-item-name-${idx}`} value={it.item_name} onChange={e => updateItem(idx, { item_name: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" placeholder="Item name" />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 px-2 rounded hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-slate-500 pt-2">
          Processing as <span className="text-cyan-300">{user?.name}</span> · This action will be saved in history.
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2 sticky bottom-0 bg-white dark:bg-slate-950">
          <Button
            data-testid="cpf-not-now-btn"
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => save(false)}
            className="h-12 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            <Save className="w-4 h-4 mr-2" /> Not Now
          </Button>
          <Button
            data-testid="cpf-mark-pickup-btn"
            type="button"
            disabled={saving}
            onClick={() => save(true)}
            className="h-12 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-bold"
          >
            <PackageCheck className="w-4 h-4 mr-2" /> Mark For Pick-Up
          </Button>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="w-full text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
        >
          Back
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
